/**
 * Establish an authenticated session from a license key after server-side validation.
 */

import { validateKey } from './api';
import { clearCode, clearKey, saveKey } from './storage';
import { updateAuthState } from './authManager';

/**
 * Validates the key with the license server, persists it, and updates in-memory auth state.
 */
export async function establishLicenseSession(licenseKey: string): Promise<void> {
  const trimmed = licenseKey.trim();
  if (!trimmed) {
    throw new Error('License key is required');
  }

  const ok = await validateKey(trimmed);
  if (!ok) {
    throw new Error('Invalid license key');
  }

  saveKey(trimmed);
  updateAuthState(true, trimmed);
}

/**
 * Clears persisted license credentials (both the JWT and the redemption
 * code) and in-memory auth. Call on sign-out or when switching accounts.
 * Clearing both ensures the next signed-in user starts from a clean
 * slate — without this, a stale code from the previous user would be
 * re-minted on next boot.
 */
export function clearLicenseSession(): void {
  clearKey();
  clearCode();
  updateAuthState(false, null);
}
